import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Icon } from '../components/Icon';
import { useI18n } from '../i18n';
import { useApp } from './AppState';
import { useRouter } from './router';
import { tabForPath } from './tabTips';

/**
 * What this tab is for — said once, in a bubble sitting on the dock.
 *
 * Jay: *"a description of the usefulness of that tab appears right above that
 * little tab area at the bottom, the same colour as the tab when it's selected,
 * with a little x to close it … as they go through all the different tabs they're
 * able to close that description, and as they close each one they don't see it
 * again."*
 *
 * So the rules are his, and there are only three:
 *
 *   - **It shows whenever you are on a tab whose description is still open** —
 *     the first time you arrive, and every time after, until *you* close it. It
 *     never times itself out: a sentence that vanishes while somebody is still
 *     reading it is worse than no sentence, and the people this is for read
 *     slowly.
 *   - **The × closes that one for good**, on this device, and only that one. The
 *     next tab still introduces itself.
 *   - **Settings can bring them all back** (*Show the tab descriptions again*),
 *     which is also how a second person on the same tablet gets to see them.
 *
 * It wears the selected tab's own colours (`--primary-soft-2` behind,
 * `--primary-strong` for the name) and a small tail pointing down at the lit
 * button, so there is no doubt which picture in the row the words are about. The
 * tail is measured rather than guessed: the dock is centred and its buttons come
 * and go with the mode, so "the third button" is nowhere in particular.
 *
 * Anything that slides up from the bottom has to clear it, so its height is
 * published as `--tab-tip-h` and the toasts sit above it (components.css).
 */
export function TabTip() {
  const { t, lang } = useI18n();
  const { path } = useRouter();
  const { closedTabTips, closeTabTip, textSize } = useApp();
  const tab = tabForPath(path);
  const showing = !!tab && !closedTabTips.includes(tab.id);
  const bubble = useRef<HTMLElement | null>(null);
  /** Where the tail goes, in pixels from the bubble's left edge — or null when there is no lit button to point at (Simple mode's dock is only the +). */
  const [tail, setTail] = useState<number | null>(null);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!showing) {
      root.style.removeProperty('--tab-tip-h');
      return undefined;
    }
    const measure = () => {
      const el = bubble.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      // The gap under it included, so whatever stacks above lands clear of the tail too.
      root.style.setProperty('--tab-tip-h', `${Math.round(box.height) + 12}px`);
      const lit = document.querySelector<HTMLElement>('.dock .dock-btn[aria-current="page"]');
      if (!lit) return setTail(null);
      const b = lit.getBoundingClientRect();
      // Kept off the rounded corners, where a tail looks broken.
      setTail(Math.min(box.width - 28, Math.max(28, b.left + b.width / 2 - box.left)));
    };
    measure();
    /*
     * And again whenever the lit button can have moved without this component
     * hearing about it. Switching language is the one that matters: the page's
     * direction is flipped on <html> by somebody else's effect, *after* this one
     * has run, and the dock mirrors itself under a tail that was measured a
     * moment too early. So the dock and the bubble are watched for size, and
     * <html> for its direction.
     */
    const frame = requestAnimationFrame(measure);
    const sizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (bubble.current) sizes?.observe(bubble.current);
    const dock = document.querySelector('.dock');
    if (dock) sizes?.observe(dock);
    const direction = new MutationObserver(measure);
    direction.observe(root, { attributes: true, attributeFilter: ['dir'] });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      sizes?.disconnect();
      direction.disconnect();
      window.removeEventListener('resize', measure);
      root.style.removeProperty('--tab-tip-h');
    };
    // Everything that can move the lit button or change the bubble's size.
  }, [showing, tab?.id, lang, textSize]);

  if (!tab || !showing) return null;

  return (
    <aside
      // A new bubble per tab, so each one arrives rather than the words changing under your eyes.
      key={tab.id}
      ref={bubble}
      className="tab-tip"
      role="status"
      aria-live="polite"
      style={tail === null ? undefined : ({ '--tail-x': `${Math.round(tail)}px` } as CSSProperties)}
    >
      <span className="tab-tip-icon" aria-hidden="true"><Icon name={tab.icon} /></span>
      <div className="tab-tip-text">
        <p className="tab-tip-title">{t(tab.key)}</p>
        <p className="tab-tip-body">{t(tab.tip)}</p>
      </div>
      <button type="button" className="tab-tip-x" onClick={() => closeTabTip(tab.id)} aria-label={t('tabTip.close')} title={t('tabTip.close')}>
        <Icon name="close" />
      </button>
      {tail !== null && <span className="tab-tip-tail" aria-hidden="true" />}
    </aside>
  );
}
